import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { formatDate } from '@superdispatch/dates';
import { SuspendedPhoneText } from '@superdispatch/phones';
import { ColorDynamic, Stack, Tag } from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  onboardingPacketsPageParamsSchema,
  OnboardingPacketWithShipperDTO,
} from './data/OnboardingPacketDTO';
import { getOnboardingPacketStatusConfig } from './data/OnboardingPacketStatus';
import {
  ONBOARDING_PACKETS_PAGE_SIZE,
  useOnboardingPacketsWithShippers,
} from './data/OnboardingPacketsAPI';
import { useLocationParams } from 'shared/routing/LocationParams';
import { joinStrings } from 'shared/utils/StringUtils';
import { ShipperProfileLink } from 'shared/modules/shipper/ShipperProfileLink';
import { OnboardingPacketStatus } from 'shared/modules/onboarding-packets/data/OnboardingPacketDTO';

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 4px;
  border: 1px solid ${ColorDynamic.Silver400};
  background-color: ${ColorDynamic.White};
`;

const HeaderCell = styled(TableCell)`
  padding: 16px;
  border-bottom: 1px solid ${ColorDynamic.Silver400};
`;

const BodyCell = styled(TableCell)`
  padding: 16px;
  border-bottom: 1px solid ${ColorDynamic.Silver400};

  .MuiTableRow-root:last-child > & {
    border-bottom: none;
  }
`;

const PaginationFooter = styled.div`
  padding: 16px;
  border-top: 1px solid ${ColorDynamic.Silver400};
`;

interface OnboardingPacketsTableProps {
  status?: OnboardingPacketStatus;
}

export function OnboardingPacketsTable({
  status,
}: OnboardingPacketsTableProps) {
  const [params, updateParams] = useLocationParams({
    zodSchema: onboardingPacketsPageParamsSchema,
  });

  const { data: packets } = useOnboardingPacketsWithShippers({
    page: params.page,
    limit: ONBOARDING_PACKETS_PAGE_SIZE,
    status,
  });

  return (
    <TableContainer>
      <Table aria-label="Onboarding packets">
        <TableHead>
          <TableRow>
            <HeaderCell>
              <TextBox variant="heading-5" color="secondary">
                Name
              </TextBox>
            </HeaderCell>
            <HeaderCell>
              <TextBox variant="heading-5" color="secondary">
                City, State
              </TextBox>
            </HeaderCell>
            <HeaderCell>
              <TextBox variant="heading-5" color="secondary">
                Contact
              </TextBox>
            </HeaderCell>
            <HeaderCell>
              <TextBox variant="heading-5" color="secondary">
                Status
              </TextBox>
            </HeaderCell>
            <HeaderCell aria-label="Actions" />
          </TableRow>
        </TableHead>

        <TableBody>
          {packets?.data.map((packet) => (
            <OnboardingPacketsTableRow key={packet.guid} packet={packet} />
          ))}
        </TableBody>
      </Table>

      {packets ? (
        <PaginationFooter>
          <Pagination
            page={params.page}
            count={Math.ceil(
              packets.pagination.count / ONBOARDING_PACKETS_PAGE_SIZE,
            )}
            siblingCount={1}
            onChange={(_event, nextPage) => {
              updateParams({ page: nextPage });
            }}
          />
        </PaginationFooter>
      ) : (
        <Box padding="small">
          <Stack align="center">
            <CircularProgress />
          </Stack>
        </Box>
      )}
    </TableContainer>
  );
}

interface OnboardingPacketsTableRowProps {
  packet: OnboardingPacketWithShipperDTO;
}

function OnboardingPacketsTableRow({ packet }: OnboardingPacketsTableRowProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { label, color } = getOnboardingPacketStatusConfig(packet.status);
  const { shipper } = packet;
  const canOnlyView = packet.status !== 'pending';

  return (
    <TableRow>
      <BodyCell>
        {!!shipper && (
          <ShipperProfileLink guid={shipper.guid} medium="Onboarding Packets">
            {shipper.name}
          </ShipperProfileLink>
        )}
      </BodyCell>
      <BodyCell>
        <TextBox>{joinStrings(', ', shipper?.city, shipper?.state)}</TextBox>
      </BodyCell>
      <BodyCell>
        <Stack space="xxsmall">
          {!!shipper?.contact_phone && (
            <TextBox>
              <SuspendedPhoneText
                phone={shipper.contact_phone}
                fallback={shipper.contact_phone}
              />
            </TextBox>
          )}
          {shipper?.contact_email}
        </Stack>
      </BodyCell>
      <BodyCell>
        <Tooltip
          placement="top"
          title={`Sent on ${formatDate(packet.created_at, { variant: 'Date' })}`}
        >
          <Box display="inline-block">
            <Tag color={color} variant="subtle">
              {label}
            </Tag>
          </Box>
        </Tooltip>
      </BodyCell>
      <BodyCell align="right">
        <Button
          variant="neutral"
          onClick={() => {
            const backUrl = new URLSearchParams({
              back_url: location.pathname + location.search,
            });
            navigate(
              `/onboarding-packets/${packet.guid}/${canOnlyView ? 'view' : 'form'}?${backUrl.toString()}`,
            );
          }}
        >
          {canOnlyView ? 'View' : 'Fill Out'}
        </Button>
      </BodyCell>
    </TableRow>
  );
}
